<template>
  <section class="app-table-wrap invoices-table-wrap">
    <div class="app-table-scroll">
      <table class="app-data-table">
        <thead>
          <tr>
            <th>
              <button
                type="button"
                class="invoices-table-sort-btn"
                :title="labels.sortTitles.date"
                @click="emitSort('date')"
              >
                <span>{{ labels.columns.date }}</span>
                <i class="fa" :class="sortIconClass('date')" aria-hidden="true"></i>
              </button>
            </th>
            <th>
              <button
                type="button"
                class="invoices-table-sort-btn"
                :title="labels.sortTitles.customer"
                @click="emitSort('customer')"
              >
                <span>{{ labels.columns.customer }}</span>
                <i class="fa" :class="sortIconClass('customer')" aria-hidden="true"></i>
              </button>
            </th>
            <th>
              <button
                type="button"
                class="invoices-table-sort-btn"
                :title="labels.sortTitles.folio"
                @click="emitSort('folio')"
              >
                <span>{{ labels.columns.folio }}</span>
                <i class="fa" :class="sortIconClass('folio')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="app-text-right">
              <button
                type="button"
                class="invoices-table-sort-btn invoices-table-sort-btn--right"
                :title="labels.sortTitles.total"
                @click="emitSort('total')"
              >
                <span>{{ labels.columns.total }}</span>
                <i class="fa" :class="sortIconClass('total')" aria-hidden="true"></i>
              </button>
            </th>
            <th class="app-text-right">
              <button
                type="button"
                class="invoices-table-sort-btn invoices-table-sort-btn--right"
                :title="labels.sortTitles.balance"
                @click="emitSort('balance')"
              >
                <span>{{ labels.columns.balance }}</span>
                <i class="fa" :class="sortIconClass('balance')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ labels.columns.status }}</th>
            <th>{{ labels.columns.payment }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="8" class="app-table-empty">{{ loadingText }}</td>
          </tr>

          <tr v-else-if="normalizedRows.length === 0">
            <td :colspan="8" class="app-table-empty">{{ emptyText }}</td>
          </tr>

          <tr v-else v-for="(row, index) in normalizedRows" :key="row.id || index">
            <td>{{ formatDate(row.date) }}</td>
            <td>
              <div class="expenses-cell-stack">
                <a
                  v-if="row?.customer_id"
                  :href="resolveCustomerHref(row)"
                  class="expenses-table-link expenses-cell-main"
                  :title="labels.links.customerDetail"
                  @click="navigateToCustomerDetail(row, $event)"
                >
                  {{ resolveCustomerName(row) }}
                </a>
                <span v-else class="expenses-cell-main">{{ resolveCustomerName(row) }}</span>

                <a
                  v-if="row?.sale_id"
                  :href="resolveSaleHref(row)"
                  class="expenses-table-link expenses-cell-sub"
                  :title="labels.links.saleDetail"
                  @click="navigateToSaleDetail(row, $event)"
                >
                  {{ resolveSaleDisplayName(row) }}
                </a>
                <span v-else class="expenses-cell-sub">{{ resolveSaleDisplayName(row) }}</span>
              </div>
            </td>
            <td>
              <a
                v-if="row?.id"
                :href="resolveSimpleNoteHref(row)"
                class="expenses-table-link"
                :title="labels.actions.view"
                @click="navigateToSimpleNoteDetail(row, $event)"
              >
                <strong>{{ resolveSerialFolio(row) }}</strong>
              </a>
              <strong v-else>{{ resolveSerialFolio(row) }}</strong>
            </td>
            <td class="app-text-right app-strong">{{ formatAmountWithCurrency(row.total, row.currency) }}</td>
            <td class="app-text-right app-strong">{{ formatAmountWithCurrency(row.balance, row.currency) }}</td>
            <td>
              <span class="app-status-pill expenses-status-pill" :class="resolveStatusClass(row)">
                {{ resolveStatusLabel(row) }}
              </span>
            </td>
            <td>
              <button
                v-if="showPaymentAction(row)"
                type="button"
                class="expenses-action-link"
                @click="$emit('pay-note', row)"
              >
                {{ labels.actions.pay }}
              </button>
              <a
                v-else-if="hasPaymentReceiptRoute(row)"
                :href="resolvePaymentHref(row)"
                class="expenses-table-link"
                @click="navigateToPaymentDetail(row, $event)"
              >
                {{ resolvePaymentReference(row) }}
              </a>
              <span v-else>{{ resolvePaymentReference(row) }}</span>
            </td>
            <td class="app-table-actions-cell">
              <div class="app-table-actions-inline">
                <button
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--view"
                  :title="labels.actions.view"
                  @click="$emit('view-note', row)"
                >
                  <i class="fa fa-eye" aria-hidden="true"></i>
                </button>

                <button
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--download invoices-table-icon-btn--download"
                  :title="labels.actions.download"
                  @click="$emit('download-note', row)"
                >
                  <i class="fa fa-file-pdf-o" aria-hidden="true"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="normalizedPagination.total_pages > 1" class="app-table-footer">
      <AppPagination
        :current-page="normalizedPagination.current_page"
        :total-pages="normalizedPagination.total_pages"
        @page-change="requestPage"
      />
    </div>
  </section>
</template>

<script>
import AppPagination from '@/components/common/AppPagination.vue';
import { formatDateShort } from '@/utils/datePresentation';
import { formatAmountWithCurrency } from '@/utils/numberFormat';
import { resolveSortIconClass, resolveSortTogglePayload } from '@/composables/shared/useSorting';
import {
  navigateToRouteDetail,
  resolveRouteHref,
} from '@/composables/shared/useNavigation';

export default {
  name: 'SimpleNotesTable',
  components: {
    AppPagination,
  },
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: Object,
      required: true,
    },
    loadingText: {
      type: String,
      required: true,
    },
    emptyText: {
      type: String,
      required: true,
    },
    pagination: {
      type: Object,
      default: () => ({
        current_page: 1,
        per_page: 20,
        total_pages: 1,
        total_items: 0,
      }),
    },
    sortField: {
      type: String,
      default: '',
    },
    sortDirection: {
      type: String,
      default: 'asc',
    },
  },
  emits: ['view-note', 'download-note', 'pay-note', 'page-change', 'sort-change'],
  computed: {
    normalizedRows() {
      return Array.isArray(this.rows) ? this.rows : [];
    },
    normalizedPagination() {
      const raw = this.pagination || {};

      return {
        current_page: Number(raw.current_page || 1),
        per_page: Number(raw.per_page || 20),
        total_pages: Number(raw.total_pages || 1),
        total_items: Number(raw.total_items || 0),
      };
    },
  },
  methods: {
    formatAmountWithCurrency,
    formatDate(value) {
      return formatDateShort(value);
    },
    resolveCustomerName(row) {
      return String(row?.customer_name || row?.company_name || this.resolveEmptyValue()).trim() || this.resolveEmptyValue();
    },
    resolveSaleDisplayName(row) {
      const fallback = String(this.labels?.values?.noBusiness || 'Sin negocio').trim() || 'Sin negocio';
      return String(row?.sale_name || row?.business_name || fallback).trim() || fallback;
    },
    resolveSerialFolio(row) {
      const serial = String(row?.serial || 'NS').trim();
      const folio = String(row?.folio || this.labels?.values?.emptyValue || '-').trim();
      return `${serial}${folio}`;
    },
    resolveStatusLabel(row) {
      const fallback = String(this.labels?.values?.emptyValue || '-').trim() || '-';
      return String(row?.status || fallback).trim() || fallback;
    },
    resolveStatusCategory(row) {
      const normalized = String(row?.status_key || row?.status || '').trim().toLowerCase();

      if (['paid', 'done', 'cobrada', 'pagada'].includes(normalized)) {
        return 'paid';
      }

      if (['canceled', 'cancelada'].includes(normalized)) {
        return 'canceled';
      }

      if (['partial', 'parcial', 'partially_paid', 'partially-paid', 'parcialmente pagada'].includes(normalized)) {
        return 'partial';
      }

      return 'pending';
    },
    resolveStatusClass(row) {
      const status = this.resolveStatusCategory(row);
      if (status === 'paid') {
        return 'simple-notes-status-pill--paid';
      }

      if (status === 'canceled') {
        return 'simple-notes-status-pill--canceled';
      }

      if (status === 'partial') {
        return 'simple-notes-status-pill--partial';
      }

      return 'simple-notes-status-pill--pending';
    },
    resolvePaymentReceiptId(row) {
      const directId = Number(row?.payment_receipt_id || row?.payment_receipt?.id || 0) || 0;
      if (directId > 0) {
        return directId;
      }

      const paymentItems = Array.isArray(row?.payment)
        ? row.payment
        : (Array.isArray(row?.payments) ? row.payments : []);
      const nestedId = paymentItems
        .map((item) => Number(item?.payment_receipt_id || item?.id || 0) || 0)
        .find((value) => value > 0) || 0;

      return nestedId;
    },
    hasPaymentReceiptRoute(row) {
      return this.resolvePaymentReceiptId(row) > 0;
    },
    showPaymentAction(row) {
      const status = this.resolveStatusCategory(row);
      const hasRelatedSale = Number(row?.sale_id || 0) > 0 || row?.has_sale === true;
      return hasRelatedSale && status !== 'paid' && status !== 'canceled';
    },
    resolvePaymentReference(row) {
      const collected = [];

      const addValue = (value) => {
        const normalized = String(value || '').trim();
        if (normalized !== '') {
          collected.push(normalized);
        }
      };

      if (Array.isArray(row?.payment)) {
        row.payment.forEach((item) => {
          addValue(item?.code);
        });
      } else {
        addValue(row?.payment?.code);
      }

      if (Array.isArray(row?.payments)) {
        row.payments.forEach((item) => {
          addValue(item?.code);
        });
      }

      if (Array.isArray(row?.payment_receipt_codes)) {
        row.payment_receipt_codes.forEach((item) => addValue(item));
      }

      addValue(row?.payment_receipt_code);
      addValue(row?.payment_code);

      const uniqueValues = [...new Set(collected)];
      return uniqueValues[0] || '';
    },
    resolveEmptyValue() {
      return String(this.labels?.values?.emptyValue || '-').trim() || '-';
    },
    resolveCustomerHref(row) {
      return resolveRouteHref(this.$router, 'customerDetail', 'customerId', row?.customer_id);
    },
    resolveSaleHref(row) {
      return resolveRouteHref(this.$router, 'saleDetail', 'saleId', row?.sale_id);
    },
    resolveSimpleNoteHref(row) {
      return resolveRouteHref(this.$router, 'simpleNoteDetail', 'simpleNoteId', row?.id);
    },
    resolvePaymentHref(row) {
      return resolveRouteHref(
        this.$router,
        'paymentReceiptDetail',
        'paymentReceiptId',
        this.resolvePaymentReceiptId(row),
      );
    },
    navigateToCustomerDetail(row, event) {
      navigateToRouteDetail(this.$router, 'customerDetail', 'customerId', row?.customer_id, event);
    },
    navigateToSaleDetail(row, event) {
      navigateToRouteDetail(this.$router, 'saleDetail', 'saleId', row?.sale_id, event);
    },
    navigateToSimpleNoteDetail(row, event) {
      const isModifiedClick = Boolean(
        event
        && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      );
      if (isModifiedClick) {
        return;
      }

      if (event) {
        event.preventDefault();
      }

      this.$emit('view-note', row);
    },
    navigateToPaymentDetail(row, event) {
      navigateToRouteDetail(
        this.$router,
        'paymentReceiptDetail',
        'paymentReceiptId',
        this.resolvePaymentReceiptId(row),
        event,
      );
    },
    emitSort(field) {
      const payload = resolveSortTogglePayload(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );

      if (!payload) {
        return;
      }

      this.$emit('sort-change', payload);
    },
    sortIconClass(field) {
      return resolveSortIconClass(
        {
          field: this.sortField,
          direction: this.sortDirection,
        },
        field,
      );
    },
    requestPage(page) {
      this.$emit('page-change', page);
    },
  },
};
</script>

<style scoped>
.invoices-table-sort-btn {
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
}

.invoices-table-sort-btn span {
  text-transform: uppercase;
}

.invoices-table-sort-btn--right {
  margin-left: auto;
}

.expenses-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.expenses-cell-main {
  font-weight: 600;
}

.expenses-cell-sub {
  color: #6b7280;
  font-size: 12px;
}

.simple-notes-status-pill--paid {
  color: #1c9b43;
  background: rgba(59, 212, 46, 0.14);
  border: 1px solid rgba(59, 212, 46, 0.3);
}

.simple-notes-status-pill--pending {
  color: #b45309;
  background: #fff7e8;
  border: 1px solid rgba(251, 191, 36, 0.55);
}

.simple-notes-status-pill--partial {
  color: #1d4ed8;
  background: rgba(37, 99, 235, 0.12);
  border: 1px solid rgba(37, 99, 235, 0.28);
}

.simple-notes-status-pill--canceled {
  color: #b42318;
  background: #fff0f0;
  border: 1px solid rgba(197, 58, 58, 0.3);
}

.invoices-table-icon-btn--download {
  color: inherit;
}

.invoices-table-icon-btn--download i {
  color: #b45309;
}

.invoices-table-icon-btn--download:hover,
.invoices-table-icon-btn--download:focus {
  color: inherit;
}

.invoices-table-icon-btn--download:hover i,
.invoices-table-icon-btn--download:focus i {
  color: #92400e;
}
</style>
